"use client"

import { Controller, useFormContext } from "react-hook-form"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Trash2, AlertCircle, Clock } from "lucide-react"
import { Separator } from '@/components/ui/separator'
import { Input } from "@/components/ui/input"
import { Facility } from "@/types/facility"
import { Label } from "@/components/ui/label"
import { cn } from "@/lib/utils"
import { useEffect, useCallback, useRef } from "react"

interface FormStepProps {
  action: "add" | "edit";
  open: boolean;
}       

export const AvailabilityForm = ({
  action,
  open,
}: FormStepProps) => {
    const {
        control,
        formState: { errors },
        setValue,
        watch,
        trigger,
    } = useFormContext<Facility>();

    const days = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"] as const;
    const dayNames = {
        monday: "Monday",
        tuesday: "Tuesday", 
        wednesday: "Wednesday",
        thursday: "Thursday",
        friday: "Friday",
        saturday: "Saturday",
        sunday: "Sunday",
    };

    // Validation function to check if at least one day is selected
    const validateAtLeastOneDay = (availability: any): string | true => {
        if (!availability) return "At least one day must be selected";
        
        const hasAtLeastOneDay = Object.values(availability).some(
            (day: any) => day && day.is_available === true
        );
        
        if (!hasAtLeastOneDay) {
            return "At least one day must be selected";
        }
        
        return true;
    };

    // Helper function to convert time string to minutes
    const timeToMinutes = (timeString: string): number => {
        if (!timeString) return 0;
        const [hours, minutes] = timeString.split(":").map(Number);
        return hours * 60 + minutes;
    };

    // Helper function to check if time range spans midnight (overnight)
    const isOvernightRange = (startTime: string, endTime: string): boolean => {
        if (!startTime || !endTime) return false;
        const startMinutes = timeToMinutes(startTime);
        const endMinutes = timeToMinutes(endTime);
        return endMinutes <= startMinutes; // If end <= start, it's overnight
    };

    // Helper function to get total duration in minutes for a time range (handles overnight)
    const getDurationMinutes = (startTime: string, endTime: string): number => {
        if (!startTime || !endTime) return 0;
        const startMinutes = timeToMinutes(startTime);
        const endMinutes = timeToMinutes(endTime);
        
        if (isOvernightRange(startTime, endTime)) {
            // Overnight: end time is next day, so add 24 hours
            return (24 * 60) - startMinutes + endMinutes;
        }
        return endMinutes - startMinutes;
    };

    const validateTimeRange = (startTime: string, endTime: string): boolean => {
        if (!startTime || !endTime) return true;
        try {
            // Allow overnight ranges (end <= start means next day)
            // For same-day ranges, end must be > start
            const isOvernight = isOvernightRange(startTime, endTime);
            if (isOvernight) {
                return true; // Overnight ranges are valid
            }
            
            // For same-day ranges, end must be after start
            const startMinutes = timeToMinutes(startTime);
            const endMinutes = timeToMinutes(endTime);
            return endMinutes > startMinutes;
        } catch (error) {
            return true; // Don't show error for malformed times during input
        }
    }

    const calculateEndTime = (startTime: string, durationPerSlot: number = 1): string => {
        if (!startTime) return ""
        const [hours, minutes] = startTime.split(":").map(Number)
        const totalStartMinutes = hours * 60 + minutes
        const totalEndMinutes = totalStartMinutes + (durationPerSlot * 60)
        
        const endHours = Math.floor(totalEndMinutes / 60)
        const endMinutes = totalEndMinutes % 60
        
        return `${String(endHours).padStart(2, '0')}:${String(endMinutes).padStart(2, '0')}`
    }


    const isTimeRangeExact = (startTime: string, endTime: string, durationPerSlot: number): boolean => {
        if (!startTime || !endTime || !durationPerSlot) return true;
        
        const [startHours, startMinutes] = startTime.split(":")
        const [endHours, endMinutes] = endTime.split(":")
        
        const start = parseInt(startHours) * 60 + parseInt(startMinutes)
        const end = parseInt(endHours) * 60 + parseInt(endMinutes)
        
        const totalMinutes = end - start;
        const durationInMinutes = durationPerSlot * 60;
        
        // Check if the time range exactly matches the duration per slot
        return totalMinutes === durationInMinutes;
    }

    const calculateTimeSlots = (startTime: string, endTime: string, durationPerSlot: number): string[] => {
        if (!startTime || !endTime || !durationPerSlot) return [];

        const slots: string[] = [];
        
        // Check if this is an overnight range
        const isOvernight = isOvernightRange(startTime, endTime);
        
        // Convert times to minutes
        let currentMinutes = timeToMinutes(startTime);
        let totalEndMinutes = timeToMinutes(endTime);
        
        // If overnight, adjust end time to be next day (add 24 hours)
        if (isOvernight) {
            totalEndMinutes += 24 * 60; // Add 24 hours for next day
        }
        
        const slotDurationMinutes = durationPerSlot * 60;

        // Calculate slots until we can't fit another one
        while (currentMinutes + slotDurationMinutes <= totalEndMinutes) {
            // Calculate slot start time (modulo 24 hours to handle overnight)
            const slotStartMinutesOfDay = currentMinutes % (24 * 60);
            const slotStartHours = Math.floor(slotStartMinutesOfDay / 60);
            const slotStartMinutes = slotStartMinutesOfDay % 60;
            const slotStart = `${slotStartHours.toString().padStart(2, "0")}:${slotStartMinutes.toString().padStart(2, "0")}`;

            // Calculate slot end time (modulo 24 hours to handle overnight)
            const slotEndTotalMinutes = currentMinutes + slotDurationMinutes;
            const slotEndMinutesOfDay = slotEndTotalMinutes % (24 * 60);
            const slotEndHours = Math.floor(slotEndMinutesOfDay / 60);
            const slotEndMinutesRemainder = slotEndMinutesOfDay % 60;
            const slotEnd = `${slotEndHours.toString().padStart(2, "0")}:${slotEndMinutesRemainder.toString().padStart(2, "0")}`;

            slots.push(`${slotStart} - ${slotEnd}`);
            currentMinutes += slotDurationMinutes;
        }

        return slots;
    }

    const validationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const debouncedValidation = useCallback(() => {
        if (validationTimeoutRef.current) {
            clearTimeout(validationTimeoutRef.current);
        }
        validationTimeoutRef.current = setTimeout(() => {
            trigger();
        }, 150);
    }, [trigger]);

    const toggleDay = (day: typeof days[number]) => {
        const isCurrentlyAvailable = watch(`availability.${day}.is_available` as any);
        setValue(`availability.${day}.is_available` as any, !isCurrentlyAvailable);
        
        if (!isCurrentlyAvailable) {
            // When enabling a day, set default times if empty
            const startTime = watch(`availability.${day}.time_slots.start_time` as any);
            const endTime = watch(`availability.${day}.time_slots.end_time` as any);
            const durationPerSlot = watch("duration_per_slot") || 1; // Default to 1 hour if not set
            
            if (!startTime) {
                setValue(`availability.${day}.time_slots.start_time` as any, "09:00");
            }
            if (!endTime) {
                // Calculate end time based on duration per slot
                const defaultStartTime = startTime || "09:00";
                const calculatedEndTime = calculateEndTime(defaultStartTime, durationPerSlot);
                setValue(`availability.${day}.time_slots.end_time` as any, calculatedEndTime);
            }
        }
        
        // Use debounced validation
        debouncedValidation();
    }

    const clearDayTimes = (day: typeof days[number]) => {
        setValue(`availability.${day}.is_available` as any, false);
        setValue(`availability.${day}.time_slots.start_time` as any, "");
        setValue(`availability.${day}.time_slots.end_time` as any, "");
        
        // Use debounced validation
        debouncedValidation();
    }

    const validateDurationRequiredForAvailableDays = (durationPerSlot: number): string | true => {
        // Check if any day is available
        const hasAvailableDay = days.some(day => {
            const dayData = watch(`availability.${day}`);
            return dayData?.is_available;
        });

        if (hasAvailableDay && (!durationPerSlot || durationPerSlot <= 0)) {
            return "Duration per slot is required for available days.";
        }

        return true;
    };

    const validateDurationAgainstTimeRanges = (durationPerSlot: number): string | true => {
        if (!durationPerSlot || durationPerSlot <= 0) return true;
        
        for (const day of days) {
            const dayData = watch(`availability.${day}`);
            if (dayData?.is_available) {
                const startTime = dayData.time_slots?.start_time;
                const endTime = dayData.time_slots?.end_time;
                
                // Check if both times are provided
                if (!startTime || !endTime) {
                    return "Please select both start and end time to set duration per slot.";
                }
                
                try {
                    // Calculate total time in minutes (handles overnight ranges)
                    const totalTimeMinutes = getDurationMinutes(startTime, endTime);
                    const durationMinutes = durationPerSlot * 60;
                    
                    // Check if duration exceeds total time
                    if (durationMinutes > totalTimeMinutes) {
                        return "Duration exceeds available time range.";
                    }
                    
                    // Check if time range is evenly divisible by duration
                    // if (totalTimeMinutes % durationMinutes !== 0) {
                    //     const slots = Math.floor(totalTimeMinutes / durationMinutes);
                    //     const remainingMinutes = totalTimeMinutes % durationMinutes;
                    //     return `Time range is not evenly divisible by duration. ${slots} complete slots with ${remainingMinutes} minutes remaining.`;
                    // }
                } catch (error) {
                    return "Invalid time format";
                }
            }
        }
        return true;
    };

    const recalculateEndTimes = useCallback(() => {
        const durationPerSlot = watch("duration_per_slot");
        if (!durationPerSlot || durationPerSlot <= 0) return;

        for (const day of days) {
            const dayData = watch(`availability.${day}`);
            if (dayData?.is_available && dayData?.time_slots?.start_time && !dayData?.time_slots?.end_time) {
                // Only auto-calculate end time if it's empty to avoid overriding user input
                const startTime = dayData.time_slots.start_time;
                const calculatedEndTime = calculateEndTime(startTime, durationPerSlot);
                setValue(`availability.${day}.time_slots.end_time` as any, calculatedEndTime);
            }
        }
        
        // Use debounced validation
        debouncedValidation();
    }, [watch, setValue, days, debouncedValidation]);

    // Watch for duration per slot changes
    const durationPerSlot = watch("duration_per_slot");
    
    useEffect(() => {
        if (durationPerSlot && durationPerSlot > 0) {
            recalculateEndTimes();
        }
    }, [durationPerSlot]);

    // Trigger validation on mount to show error if no days are selected
    useEffect(() => {
        const availability = watch("availability");
        if (availability) {
            const hasAtLeastOneDay = Object.values(availability).some(
                (day: any) => day && day.is_available === true
            );
            if (!hasAtLeastOneDay) {
                debouncedValidation();
            }
        }
    }, [debouncedValidation]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (validationTimeoutRef.current) {
                clearTimeout(validationTimeoutRef.current);
            }
        };
    }, []);

    return (
        <div className="space-y-4" data-component="facility-availability">
            {/* Availability Validation Controller */}
            <Controller
                control={control}
                name="availability"
                rules={{
                    validate: validateAtLeastOneDay
                }}
                render={({ fieldState: { error } }) => (
                    <>
                        {error && (
                            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-md">
                                <AlertCircle className="h-4 w-4 text-red-500" />
                                <span className="text-sm text-red-700">
                                    {error.message}
                                </span>
                            </div>
                        )}
                    </>
                )}
            />
            
            <Card className="w-full">
                <CardHeader>
                    <CardTitle className="text-base font-medium">Availability</CardTitle>
                    <Separator className="h-[1px]" />
                </CardHeader>
                <CardContent className="space-y-6">
                    {/* Duration Per Slot Section */}
                    <div className="w-[40%]">
                        <div className="space-y-2">
                            <Label className="text-base">
                                Duration Per Slot (Hours) <span className="text-red-500">*</span>
                            </Label>
                            <Controller
                                control={control}
                                name="duration_per_slot"
                                rules={{
                                    validate: {
                                        requiredForAvailableDays: (value) => validateDurationRequiredForAvailableDays(value as number),
                                        validDuration: (value) => {
                                            if (value && (value <= 0 || value > 24)) {
                                                return "Duration must be between 0.1 and 24 hours";
                                            }
                                            return true;
                                        },
                                        timeRangeCompatibility: (value) => validateDurationAgainstTimeRanges(value as number)
                                    }
                                }}
                                render={({
                                    field: { onChange, value },
                                    fieldState: { error },
                                }) => (
                                    <>
                                        <Input
                                            type="number"
                                            id="duration_per_slot"
                                            step="0.1"
                                            min="0.1"
                                            max="24"
                                            placeholder="Enter duration in hours"
                                            value={value || ""}
                                            onChange={(e) => {
                                                onChange(e);
                                                // Trigger recalculation of end times when duration changes
                                                setTimeout(() => {
                                                    recalculateEndTimes();
                                                }, 300);
                                            }}
                                            className={cn(
                                                "border-input focus-within:ring-0 h-10",
                                                error && "border-red-500"
                                            )}
                                        />
                                        {error && (
                                            <span className="text-red-500 text-xs mt-1 block">
                                                {error.message}
                                            </span>
                                        )}
                                    </>
                                )}
                            />
                        </div>
                    </div>

                    {/* Days Availability Section */}
                    <div className="space-y-4">
                        
                        <div className="flex items-center justify-between">
                            <div className="grid grid-cols-5 gap-4 items-start w-[100%]">
                                <div className="text-sm font-medium text-zinc-500">Days</div>
                                <div className="text-sm font-medium text-zinc-500">Start Time</div>
                                <div className="text-sm font-medium text-zinc-500">End Time</div>
                                <div className="text-sm font-medium text-zinc-500"></div>
                            </div>  
                            
                        </div>

                        {days.map((day) => {
                            const isAvailable = watch(`availability.${day}.is_available` as any);
                            const startTime = watch(`availability.${day}.time_slots.start_time` as any);
                            const endTime = watch(`availability.${day}.time_slots.end_time` as any);
                            const durationPerSlot = watch("duration_per_slot");
                            const timeSlots = durationPerSlot ? calculateTimeSlots(startTime, endTime, durationPerSlot) : [];

                            return (
                                <div key={day} className="mb-5 pb-5 border-b border-gray-100 last:border-b-0">
                                    {/* Day Row */}
                                    <div className="grid grid-cols-5 gap-4 items-start w-[100%]">
                                        {/* Day Toggle Button */}
                                        <div>
                                            <button
                                                type="button"
                                                onClick={() => toggleDay(day)}
                                                className={cn(
                                                    "h-10 px-4 rounded-md text-sm font-medium transition-colors flex items-center justify-center w-full",
                                                    isAvailable
                                                        ? "bg-primary text-white"
                                                        : "bg-white border border-input text-zinc-600 hover:bg-zinc-100"
                                                )}
                                            >
                                                {dayNames[day]}
                                            </button>
                                        </div>

                                        {/* Start Time */}
                                        <div className="flex flex-col">
                                            <Controller
                                                name={`availability.${day}.time_slots.start_time`}
                                                control={control}
                                                rules={{
                                                    validate: (value, formValues) => {
                                                        const dayData = formValues.availability?.[day];
                                                        if (!dayData?.is_available) return true;
                                                        if (!value) return "Start time is required for available days";
                                                        
                                                        const endTime = dayData.time_slots?.end_time;
                                                        if (endTime && !validateTimeRange(value, endTime)) {
                                                            // Check if it's an overnight range
                                                            const isOvernight = isOvernightRange(value, endTime);
                                                            if (!isOvernight) {
                                                                return "Start time must be before end time";
                                                            }
                                                        }
                                                        
                                                        return true;
                                                    }
                                                }}
                                                render={({ field, fieldState: { error } }) => (
                                                    <>
                                                        <Input
                                                            placeholder="Select Start Time"
                                                            type="time"
                                                            disabled={!isAvailable}
                                                            className={cn(
                                                                "border-input focus-within:ring-0 h-10",
                                                                !isAvailable && "opacity-50",
                                                                error && "border-red-500"
                                                            )}
                                                            {...field}
                                                            onChange={(e) => {
                                                                field.onChange(e);
                                                                if (e.target.value) {
                                                                    const currentDurationPerSlot = watch("duration_per_slot");
                                                                    // Only auto-calculate end time if duration is set and end time is empty
                                                                    const currentEndTime = watch(`availability.${day}.time_slots.end_time`);
                                                                    if (currentDurationPerSlot && !currentEndTime) {
                                                                        const newEndTime = calculateEndTime(e.target.value, currentDurationPerSlot);
                                                                        setValue(`availability.${day}.time_slots.end_time`, newEndTime);
                                                                    }
                                                                    
                                                                    // Use debounced validation
                                                                    debouncedValidation();
                                                                }
                                                            }}
                                                        />
                                                        {error && (
                                                            <span className="text-xs text-red-500 mt-1">
                                                                {error.message}
                                                            </span>
                                                        )}
                                                    </>
                                                )}
                                            />
                                        </div>

                                        {/* End Time */}
                                        <div className="flex flex-col">
                                            <Controller
                                                name={`availability.${day}.time_slots.end_time`}
                                                control={control}
                                                rules={{
                                                    validate: (value, formValues) => {
                                                        const dayData = formValues.availability?.[day];
                                                        if (!dayData?.is_available) return true;
                                                        if (!value) return "End time is required for available days";
                                                        
                                                        const startTime = dayData.time_slots?.start_time;
                                                        if (!startTime) return true;
                                                        
                                                        // Basic time range validation (allows overnight ranges)
                                                        if (!validateTimeRange(startTime, value)) {
                                                            // Check if it's an overnight range
                                                            const isOvernight = isOvernightRange(startTime, value);
                                                            if (!isOvernight) {
                                                                return "End time must be after start time";
                                                            }
                                                        }
                                                        
                                                        return true;
                                                    }
                                                }}
                                                render={({ field, fieldState: { error } }) => (
                                                    <>
                                                        <Input
                                                            placeholder="Select End Time"
                                                            type="time"
                                                            disabled={!isAvailable}
                                                            className={cn(
                                                                "border-input focus-within:ring-0 h-10",
                                                                !isAvailable && "opacity-50",
                                                                error && "border-red-500"
                                                            )}
                                                            {...field}
                                                        />
                                                        {error && (
                                                            <span className="text-xs text-red-500 mt-1">
                                                                {error.message}
                                                            </span>
                                                        )}
                                                    </>
                                                )}
                                            />
                                        </div>

                                        {/* Clear Button */}
                                        <div className="flex items-center">
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => clearDayTimes(day)}
                                                disabled={!isAvailable}
                                                className={cn(
                                                    "h-10 w-10 p-0 text-destructive",
                                                    !isAvailable && "opacity-50 cursor-not-allowed"
                                                )}
                                                title="Clear times"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </div>

                                    {/* Time Slots Preview Row - Below the day */}
                                    {isAvailable && (
                                        <div className="ml-0 mt-2">
                                            {timeSlots.length > 0 ? (
                                                <div className="space-y-1">
                                                    <div className="flex items-center gap-1 mb-1">
                                                        <Clock className="h-3 w-3 text-gray-500" />
                                                        <span className="text-xs font-medium text-gray-600">
                                                            {timeSlots.length} slot{timeSlots.length !== 1 ? "s" : ""} generated
                                                        </span>
                                                    </div>
                                                    <div className="flex flex-wrap gap-1.5">
                                                        {timeSlots.slice(0, 6).map((slot, index) => (
                                                            <div
                                                                key={index}
                                                                className="text-xs text-gray-700 bg-blue-50 border border-blue-200 px-2 py-1 rounded-md font-mono"
                                                            >
                                                                {slot}
                                                            </div>
                                                        ))}
                                                        {timeSlots.length > 6 && (
                                                            <div className="text-xs text-gray-500 px-2 py-1 rounded-md border border-dashed border-gray-300">
                                                                +{timeSlots.length - 6} more
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            ) : startTime && endTime && durationPerSlot ? (
                                                <div className="text-xs text-red-600 flex items-center">
                                                    <AlertCircle className="h-3 w-3 mr-1" />
                                                    <span className="font-medium">Duration too large for this time range</span>
                                                </div>
                                            ) : startTime || endTime ? (
                                                <div className="text-xs text-gray-400">
                                                    Configure both start and end times to see slots
                                                </div>
                                            ) : null}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </CardContent>
            </Card>
        </div>
    )
}

export default AvailabilityForm
